import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {Message} from "primeng/api";
import {IFlow} from "../../../../../data/interfaces/iflow.interface";
import {
  faCheck,
  faCircle,
  faCircleExclamation,
  faFaceSmile,
  faFilm,
  faFolderOpen,
  faMicrophone,
  faRotateRight,
  faShieldHalved,
  faSun,
  faTrashCan,
  faTriangleExclamation,
  faUpload,
  faVideo,
  faVolumeXmark,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ApiFlowDataService} from "../../../../../data/services/api/flow/api-flow-data.service";
import {ESTADOS_LX} from "../../../../../shared/ui/liga-ui";
import {ITonoMensajeLx, porMensajeLx, tonoMensajeLx} from "../../../../../shared/ui/liga-mensajes";

// Mismos tipos que aceptaba el p-fileUpload de antes.
export const TIPOS_VIDEOSELFIE = 'video/mp4,video/webm,video/ogg,video/quicktime'

// Los estilos viven en src/assets/styles/components/_liga-ui.scss (.lx-*) y
// _liga-c.scss (.lxc-*): el build de producción corta en 4 kB por componente.
@Component({
  selector: 'app-videoselfie-component',
  templateUrl: './videoselfie-component.component.html',
  styleUrls: ['./videoselfie-component.component.scss']
})
export class VideoselfieComponentComponent implements OnDestroy {
  responseMessage: Message[] = []
  showMessage = false
  @Input() data!: IFlow
  @Input() pushingId: string = ''
  @Output() onEndProcess = new EventEmitter()
  entity: FormGroup
  hasFile = false
  file!: File

  // ---------- Solo presentación (estados dentro de la tarjeta) ----------
  // Sin LoaderService: el envío se ve en el botón y el resultado en la pantalla.
  enviando = false
  resultado: '' | 'exito' | 'error' = ''
  avisoArchivo = ''
  vistaPrevia = ''
  readonly tiposAceptados = TIPOS_VIDEOSELFIE
  readonly estados = ESTADOS_LX
  readonly porMensaje = porMensajeLx
  readonly iconos = {
    video: faVideo,
    pelicula: faFilm,
    galeria: faFolderOpen,
    borrar: faTrashCan,
    error: faCircleExclamation,
    listo: faCheck,
    aviso: faTriangleExclamation,
    reintentar: faRotateRight,
    seguro: faShieldHalved,
    cerrar: faXmark
  }
  readonly consejos = [
    {icono: faFaceSmile, texto: 'Tu cara de frente y completa'},
    {icono: faSun, texto: 'Buena luz, sin contraluz'},
    {icono: faVolumeXmark, texto: 'Un lugar sin ruido'},
    {icono: faMicrophone, texto: 'Habla en voz alta y clara'}
  ]

  constructor(
    private fb: FormBuilder,
    private flowDataService: ApiFlowDataService
  ) {
    this.entity = this.fb.group({
      file: ['', [Validators.required]]
    })
  }

  protected readonly faCircle = faCircle;
  protected readonly faUpload = faUpload;
  protected readonly faTrashCan = faTrashCan;
  select(event: any){
    this.hasFile = true
    this.file = event.files[0]
    this.crearVistaPrevia()
  }

  clear() {
    this.hasFile = false
    this.revocarVistaPrevia()
  }

  save() {
    const form = new FormData()
    form.append('document', this.file)
    form.append('idDocument', this.data.id)
    if(this.data.userDocumentId) form.append('idStage', this.data.userDocumentId)
    if(this.pushingId) form.append('pushingId', this.pushingId)
    this.enviando = true
    this.resultado = ''
    this.flowDataService.uploadVideo(form).subscribe(r => {
      this.enviando = false
      this.resultado = r.error ? 'error' : 'exito'
      this.responseMessage = [
        {
          summary: r.msg,
          severity: r.error ?  'error': 'success',
          closable: false,
        }
      ]
      if (!r.error) {
        setTimeout(() => this.terminar(), 4000)
      }
    })
  }

  ngOnDestroy() {
    this.revocarVistaPrevia()
  }

  // ---------- Presentación (sin lógica de negocio) ----------

  // Un solo onEndProcess(true): el regreso automático y «Continuar» de la
  // celebración pasan por aquí (mismo evento, mismos tiempos de antes).
  private finEmitido = false
  terminar() {
    if (this.finEmitido) return
    this.finEmitido = true
    this.onEndProcess.emit(true)
  }

  get pasoActual(): number {
    return this.hasFile ? 2 : 1
  }

  get tituloHero(): string {
    if (this.resultado === 'exito') return '¡Recibimos tu video!'
    return this.hasFile ? 'Revisa tu video' : 'Graba tu videoselfie'
  }

  get subtituloHero(): string {
    if (this.resultado === 'exito') return 'Ya puedes seguir con tu solicitud.'
    if (this.hasFile) return 'Míralo antes de enviarlo: que se vea tu cara y se escuche la leyenda.'
    return this.data?.properties?.subtitle || 'Un video corto donde lees en voz alta la leyenda de abajo.'
  }

  get conCtaFijo(): boolean {
    return this.resultado !== 'exito'
  }

  get tamanoArchivo(): string {
    const bytes = this.file?.size ?? 0
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }

  tono(mensaje: Message): ITonoMensajeLx {
    return tonoMensajeLx(mensaje)
  }

  // Input nativo (cámara frontal o galería) en lugar del p-fileUpload: entrega el
  // archivo a select() igual que su evento onSelect ({files}).
  alElegirVideo(evento: Event) {
    const input = evento.target as HTMLInputElement
    const archivo = input.files?.[0]
    input.value = ''
    if (!archivo) return
    // Validación de cliente que se suma: si el sistema dice qué tipo es y no es
    // video, se avisa aquí (sin tipo se deja pasar, como antes).
    if (archivo.type && !archivo.type.startsWith('video/')) {
      this.avisoArchivo = 'Ese archivo no es un video. Graba uno nuevo o elige un video MP4, MOV o WebM.'
      return
    }
    this.avisoArchivo = ''
    this.resultado = ''
    this.responseMessage = []
    this.select({files: [archivo]})
  }

  cambiarVideo() {
    this.resultado = ''
    this.responseMessage = []
    this.clear()
  }

  private crearVistaPrevia() {
    this.revocarVistaPrevia()
    if (this.file && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      this.vistaPrevia = URL.createObjectURL(this.file)
    }
  }

  private revocarVistaPrevia() {
    if (this.vistaPrevia && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(this.vistaPrevia)
    }
    this.vistaPrevia = ''
  }
}
